/**
 * Per-app auto-sync preferences for the DJ-library watcher: when enabled,
 * changes the watcher detects in an app's library are imported automatically
 * (AutoSyncManager) instead of waiting for a "Sync now" click. `includeNew`
 * additionally lets never-imported playlists arrive without going through the
 * review picker — off by default, so bulk crate creation in the DJ app can't
 * flood the library unreviewed.
 *
 * Keyed per signed-in user like the import exclusions (accounts are isolated
 * on shared machines — migration 012); userId null = the signed-out bucket.
 */
export const DJ_APPS = ['rekordbox', 'serato', 'traktor', 'virtualdj'];

const prefsKey = (app, userId) => `${app}.autoSync${userId ? `:${userId}` : ''}`;
const nudgeKey = (app, userId) => `${app}.autoSyncNudge${userId ? `:${userId}` : ''}`;

export const AUTO_SYNC_NUDGE_MAX = 2;

export function getAutoSyncPrefs(app, userId = null) {
    try {
        const v = JSON.parse(localStorage.getItem(prefsKey(app, userId)));
        return { enabled: !!v?.enabled, includeNew: !!v?.includeNew };
    } catch {
        return { enabled: false, includeNew: false };
    }
}

export function setAutoSyncPrefsStored(app, userId, prefs) {
    try {
        localStorage.setItem(prefsKey(app, userId), JSON.stringify({
            enabled: !!prefs?.enabled, includeNew: !!prefs?.includeNew,
        }));
    } catch { /* private mode etc. — worst case the toggle doesn't stick */ }
}

export function loadAllAutoSyncPrefs(userId = null) {
    const out = {};
    for (const app of DJ_APPS) out[app] = getAutoSyncPrefs(app, userId);
    return out;
}

/** Per-app timestamps of the last auto-import start, persisted so the
 *  MIN_INTERVAL throttle survives a relaunch — an in-memory ref meant every
 *  fresh launch re-imported immediately, stacking heavy main-process work
 *  onto the startup window (part of the 2026-08-25 launch freezes). */
const lastRunKey = (userId) => `djAutoSync.lastRun${userId ? `:${userId}` : ''}`;

export function getAutoSyncLastRuns(userId = null) {
    try {
        const v = JSON.parse(localStorage.getItem(lastRunKey(userId)));
        return v && typeof v === 'object' ? v : {};
    } catch {
        return {};
    }
}

export function markAutoSyncRun(app, userId = null) {
    try {
        const v = getAutoSyncLastRuns(userId);
        v[app] = Date.now();
        localStorage.setItem(lastRunKey(userId), JSON.stringify(v));
    } catch { /* private mode etc. */ }
}

/** "Turn on Auto-sync" offer after a manual change-driven sync — capped so it
 *  never becomes a nag. */
export function getAutoSyncNudgeCount(app, userId = null) {
    const n = parseInt(localStorage.getItem(nudgeKey(app, userId)), 10);
    return Number.isFinite(n) ? n : 0;
}

export function markAutoSyncNudgeShown(app, userId = null) {
    try {
        localStorage.setItem(nudgeKey(app, userId), String(getAutoSyncNudgeCount(app, userId) + 1));
    } catch { /* ignore */ }
}
